import type { Passkeyfehler } from "./passkey";

/**
 * Feste Wortlaute der Passkey-Zeremonien (oberflaeche.md §9, V-20): je Ansicht und Fehlerart eine eigene Meldung, nie ein
 * stummer Abbruch. Ohne Revierbezug (Anmeldung, Wiederherstellung, Kontoansicht, Registrierung) ohne Ansprache und
 * Anredepronomen (§2.3); in der Einladung in der Ansprachform des Reviers, solange sie nicht bekannt ist neutral.
 * `abgelehnt` hat keinen eigenen Wortlaut: Dort steht der Grund wörtlich nach „abgewiesen“ (§9, `Abweisung`).
 */
export type Ort = "anmeldung" | "einladung-annehmen" | "einladung-anmelden" | "wiederherstellung" | "registrierung" | "konto";
export type Ansprache = "Du" | "Sie" | null;

const BESTAETIGEN = "mit Fingerabdruck, Gesicht oder Geräte-PIN";
export const OHNE_NETZ = "Keine Verbindung. Bitte mit Netz noch einmal versuchen.";
export const VORHANDEN = "Auf diesem Gerät gibt es schon einen Passkey für dieses Konto.";
/** Bisherige Erklärung der Einladung ohne Passkey-Unterstützung (§7), jetzt auch als Meldung nach einem Versuch. */
export const EINLADUNG_OHNE_PASSKEY = "Dieses Gerät unterstützt keine Passkeys. Die Anmeldung geht dann über diesen Link.";
/** Registrierung ohne Passkey-Unterstützung (§9, unverändert). */
export const REGISTRIERUNG_OHNE_PASSKEY = "Die Registrierung erfordert einen Passkey. Dieses Gerät unterstützt keine Passkeys.";

function nachAnsprache(a: Ansprache, du: string, sie: string, neutral: string): string {
  return a === "Du" ? du : a === "Sie" ? sie : neutral;
}

function zeitAbgelaufen(a: Ansprache): string {
  return nachAnsprache(a, "Zeit abgelaufen. Bitte versuche es noch einmal.", "Zeit abgelaufen. Bitte versuchen Sie es noch einmal.",
    "Zeit abgelaufen. Bitte noch einmal versuchen.");
}

/** Meldung (`alert`) zu einem Fehlschlag; `null` bei `abgelehnt` (Abweisung mit Grund) und ohne Fehler. */
export function passkeymeldung(ort: Ort, fehler: Passkeyfehler | null, ansprache: Ansprache = null): string | null {
  if (!fehler || fehler === "abgelehnt") return null;
  if (fehler === "netz") return OHNE_NETZ;
  switch (ort) {
    case "anmeldung":
      if (fehler === "nicht-unterstuetzt") return "Passkeys sind in diesem Browser nicht verfügbar. Bitte Safari oder Chrome verwenden oder den Einladungslink öffnen.";
      if (fehler === "zeitueberschreitung") return "Zeit abgelaufen. Bitte noch einmal „Anmelden“ antippen.";
      return "Kein Passkey gefunden oder Anmeldung abgebrochen. Noch kein Zugang? Bitte den Einladungslink öffnen.";
    case "einladung-annehmen":
    case "einladung-anmelden":
      if (fehler === "nicht-unterstuetzt") return EINLADUNG_OHNE_PASSKEY;
      if (fehler === "zeitueberschreitung") return zeitAbgelaufen(ansprache);
      if (ort === "einladung-anmelden") {
        return nachAnsprache(ansprache,
          "Kein Passkey gefunden oder Anmeldung abgebrochen. Noch kein Konto? Dann tippe auf „Annehmen“.",
          "Kein Passkey gefunden oder Anmeldung abgebrochen. Noch kein Konto? Dann tippen Sie auf „Annehmen“.",
          "Kein Passkey gefunden oder Anmeldung abgebrochen. Noch kein Konto? Dann „Annehmen“ antippen.");
      }
      return nachAnsprache(ansprache,
        `Passkey nicht erstellt. Tippe noch einmal auf „Annehmen“ und bestätige ${BESTAETIGEN}.`,
        `Passkey nicht erstellt. Tippen Sie noch einmal auf „Annehmen“ und bestätigen Sie ${BESTAETIGEN}.`,
        `Passkey nicht erstellt. Bitte noch einmal „Annehmen“ antippen und ${BESTAETIGEN} bestätigen.`);
    case "wiederherstellung":
    case "registrierung":
      if (fehler === "nicht-unterstuetzt") {
        return ort === "registrierung" ? REGISTRIERUNG_OHNE_PASSKEY
          : "Dieses Gerät unterstützt keine Passkeys. Ohne Passkey geht die Anmeldung nur über einen Einladungslink.";
      }
      if (fehler === "zeitueberschreitung") return "Zeit abgelaufen. Bitte mit „Senden“ einen neuen Code anfordern.";
      if (fehler === "vorhanden") return `${VORHANDEN} Die Anmeldung geht damit über „Anmelden“.`;
      return `Passkey nicht erstellt. Bitte noch einmal „Bestätigen“ antippen und ${BESTAETIGEN} bestätigen.`;
    case "konto":
      if (fehler === "nicht-unterstuetzt") return "Dieses Gerät unterstützt keine Passkeys.";
      if (fehler === "zeitueberschreitung") return zeitAbgelaufen(null);
      if (fehler === "vorhanden") return VORHANDEN;
      return `Passkey nicht erstellt. Bitte noch einmal versuchen und ${BESTAETIGEN} bestätigen.`;
  }
}
